import React, { useState, useEffect, useMemo } from 'react';
import { theme } from '../theme';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getInstrumentMultiplier } from '../utils/tradeInstruments';

// Week helpers
const getWeekMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon, ...
  // Sunday maps to previous Monday (Mon-Sat trading week)
  const diff = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const toWeekKey = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getWeekLabel = (mondayDate) => {
  const mon = new Date(mondayDate);
  const sat = new Date(mon);
  sat.setDate(mon.getDate() + 5);
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fmt(mon).replace(`, ${mon.getFullYear()}`, '')} – ${fmt(sat)}`;
};

const isCurrentWeek = (weekKey) => {
  const currentMonday = getWeekMonday(new Date());
  return weekKey === toWeekKey(currentMonday);
};

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '');
};

const renderMarkdown = (text) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    // Bold headers: **text**
    if (/^\*\*(.+)\*\*$/.test(line.trim())) {
      return <div key={i} style={{ color: '#f0f6ff', fontWeight: 700, fontSize: '13.5px', marginTop: i > 0 ? '18px' : '0', marginBottom: '6px', paddingLeft: '11px', borderLeft: '2px solid #2DD4BF' }}>{line.trim().replace(/^\*\*|\*\*$/g, '')}</div>;
    }
    // Numbered items: 1. text
    if (/^\d+\.\s/.test(line.trim())) {
      return <div key={i} style={{ paddingLeft: '6px', marginBottom: '5px' }}>{renderInlineBold(line.trim())}</div>;
    }
    // Bullet points
    if (/^[-•]\s/.test(line.trim())) {
      return <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '5px', paddingLeft: '4px' }}><span style={{ color: '#2DD4BF', flexShrink: 0, lineHeight: '1.78' }}>›</span><span>{renderInlineBold(line.trim().replace(/^[-•]\s/, ''))}</span></div>;
    }
    // Empty lines
    if (!line.trim()) return <div key={i} style={{ height: '8px' }} />;
    // Inline bold
    return <div key={i} style={{ marginBottom: '4px' }}>{renderInlineBold(line)}</div>;
  });
};

const renderInlineBold = (text) => {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, j) =>
    j % 2 === 1 ? <strong key={j} style={{ color: '#e2eaf4', fontWeight: 600 }}>{part}</strong> : <span key={j}>{part}</span>
  );
};

const getTradeKey = (trade) =>
  `${trade.Symbol}-${trade.Strike}-${trade.Expiration}-${trade.FirstBuyExecTime}`;

const WeeklyReviewScreen = ({ tradeData, tradingProfile, tradeRatings = {} }) => {
  const { currentUser, isPro } = useAuth();
  const [weeklyReviews, setWeeklyReviews] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingGoals, setEditingGoals] = useState(null);
  const [goalsText, setGoalsText] = useState('');
  const [savingGoals, setSavingGoals] = useState(false);
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [notes, setNotes] = useState({});
  const [showAllWeeks, setShowAllWeeks] = useState(false);

  // Process raw tradeData into individual trades (same pattern as StatsDashboard)
  const trades = useMemo(() => {
    if (!tradeData.length) return [];

    const allTransactions = tradeData.flatMap(trade => trade.Transactions);
    const sortedTransactions = allTransactions.sort((a, b) => new Date(a.ExecTime) - new Date(b.ExecTime));

    const processedTrades = [];
    const positions = new Map();

    sortedTransactions.forEach(transaction => {
      const contractMultiplier = getInstrumentMultiplier(transaction);
      const key = `${transaction.Symbol}-${transaction.Strike}-${transaction.Expiration}`;
      if (!positions.has(key)) {
        positions.set(key, {
          totalQuantity: 0,
          currentQuantity: 0,
          buyRecords: [],
          sellRecords: [],
        });
      }

      const position = positions.get(key);

      if (transaction.PosEffect === 'OPEN' && transaction.Side === 'BUY') {
        position.totalQuantity += transaction.Quantity;
        position.currentQuantity += transaction.Quantity;
        position.buyRecords.push({
          quantity: transaction.Quantity,
          price: transaction.Price,
          tradeDate: transaction.TradeDate,
          execTime: transaction.ExecTime,
        });
      } else if (transaction.PosEffect === 'CLOSE' && transaction.Side === 'SELL') {
        position.sellRecords.push({
          quantity: Math.abs(transaction.Quantity),
          price: transaction.Price,
          execTime: transaction.ExecTime,
          tradeDate: transaction.TradeDate,
        });

        position.currentQuantity -= Math.abs(transaction.Quantity);

        if (position.currentQuantity === 0) {
          let totalBuyQuantity = 0;
          let totalBuyCost = 0;
          const buyRecordsForCycle = [];

          while (position.buyRecords.length > 0 && totalBuyQuantity < position.totalQuantity) {
            const buyRecord = position.buyRecords.shift();
            buyRecordsForCycle.push(buyRecord);
            totalBuyQuantity += buyRecord.quantity;
            totalBuyCost += buyRecord.quantity * buyRecord.price * contractMultiplier;
          }

          let totalSellQuantity = 0;
          let totalSellProceeds = 0;
          const sellRecordsForCycle = [];

          while (position.sellRecords.length > 0 && totalSellQuantity < totalBuyQuantity) {
            const sellRecord = position.sellRecords.shift();
            sellRecordsForCycle.push(sellRecord);
            totalSellQuantity += sellRecord.quantity;
            totalSellProceeds += sellRecord.quantity * sellRecord.price * contractMultiplier;
          }

          const profitLoss = totalSellProceeds - totalBuyCost;
          const lastSellRecord = sellRecordsForCycle[sellRecordsForCycle.length - 1];

          processedTrades.push({
            Symbol: transaction.Symbol,
            Strike: transaction.Strike,
            Expiration: transaction.Expiration,
            TradeDate: buyRecordsForCycle[0].tradeDate,
            FirstBuyExecTime: buyRecordsForCycle[0].execTime,
            LastSellExecTime: lastSellRecord.execTime,
            LastSellTradeDate: lastSellRecord.tradeDate,
            profitLoss,
            Type: transaction.Type,
            Quantity: totalBuyQuantity,
            Price: buyRecordsForCycle[0].price,
          });

          position.totalQuantity = 0;
          position.currentQuantity = 0;
        }
      }
    });

    return processedTrades;
  }, [tradeData]);

  // Load weekly reviews and notes from Firestore
  useEffect(() => {
    const fetchData = async () => {
      if (currentUser) {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.weeklyReviews) setWeeklyReviews(data.weeklyReviews);
          if (data.notes) setNotes(data.notes);
        }
      }
    };
    fetchData();
  }, [currentUser]);

  // Auto-expand current week
  useEffect(() => {
    const currentWeekKey = toWeekKey(getWeekMonday(new Date()));
    setExpandedWeeks(prev => ({ ...prev, [currentWeekKey]: true }));
  }, []);

  // Get all week keys that have trades or reviews, sorted descending
  const weekKeys = useMemo(() => {
    const keys = new Set();

    // Add weeks with trades
    trades.forEach(trade => {
      const monday = getWeekMonday(new Date(trade.FirstBuyExecTime));
      keys.add(toWeekKey(monday));
    });

    // Add weeks with existing reviews
    Object.keys(weeklyReviews).forEach(key => keys.add(key));

    // Always include current week
    keys.add(toWeekKey(getWeekMonday(new Date())));

    return Array.from(keys).sort((a, b) => new Date(b) - new Date(a));
  }, [trades, weeklyReviews]);

  const computeWeeklyData = (weekKey) => {
    const monday = new Date(weekKey + 'T00:00:00');
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    const weekTrades = trades.filter(trade => {
      const tradeDate = new Date(trade.FirstBuyExecTime);
      return tradeDate >= monday && tradeDate <= sunday;
    });

    // Group by day
    const dailyMap = {};
    weekTrades.forEach(trade => {
      const dateKey = new Date(trade.FirstBuyExecTime).toISOString().split('T')[0];
      if (!dailyMap[dateKey]) {
        dailyMap[dateKey] = { trades: [], totalPL: 0, winners: 0, losers: 0 };
      }
      dailyMap[dateKey].trades.push(trade);
      dailyMap[dateKey].totalPL += trade.profitLoss;
      if (trade.profitLoss > 0) dailyMap[dateKey].winners++;
      else if (trade.profitLoss < 0) dailyMap[dateKey].losers++;
    });

    const dailyBreakdown = Object.entries(dailyMap)
      .sort(([a], [b]) => new Date(a) - new Date(b))
      .map(([date, data]) => ({
        date,
        trades: data.trades.length,
        totalPL: data.totalPL,
        winners: data.winners,
        losers: data.losers,
      }));

    const totalPL = weekTrades.reduce((sum, t) => sum + t.profitLoss, 0);
    const winners = weekTrades.filter(t => t.profitLoss > 0).length;
    const losers = weekTrades.filter(t => t.profitLoss < 0).length;
    const totalTrades = weekTrades.length;
    const winRate = totalTrades > 0 ? ((winners / totalTrades) * 100).toFixed(1) : '0';
    const totalProfits = weekTrades.filter(t => t.profitLoss > 0).reduce((sum, t) => sum + t.profitLoss, 0);
    const totalLosses = Math.abs(weekTrades.filter(t => t.profitLoss < 0).reduce((sum, t) => sum + t.profitLoss, 0));
    const profitFactor = totalLosses === 0 ? (totalProfits > 0 ? 'Inf' : '0') : (totalProfits / totalLosses).toFixed(2);
    const totalVolume = weekTrades.reduce((sum, t) => sum + (t.Quantity || 0), 0);

    const dayPLs = dailyBreakdown.map(d => d.totalPL);
    const bestDay = dayPLs.length > 0 ? Math.max(...dayPLs) : 0;
    const worstDay = dayPLs.length > 0 ? Math.min(...dayPLs) : 0;

    // Collect daily notes within the week
    const weekNotes = [];
    for (let d = new Date(monday); d <= sunday; d.setDate(d.getDate() + 1)) {
      const dateKey = d.toISOString().split('T')[0];
      if (notes[dateKey]) {
        const stripped = stripHtml(notes[dateKey]);
        if (stripped.trim()) {
          weekNotes.push({ date: dateKey, text: stripped });
        }
      }
    }

    // Compute per-symbol stats
    const symbolMap = {};
    weekTrades.forEach(t => {
      const sym = t.symbol || t.Symbol;
      if (!sym) return;
      if (!symbolMap[sym]) symbolMap[sym] = { trades: 0, wins: 0, totalPL: 0 };
      symbolMap[sym].trades++;
      if (t.profitLoss > 0) symbolMap[sym].wins++;
      symbolMap[sym].totalPL += t.profitLoss;
    });
    const bySymbol = Object.entries(symbolMap)
      .map(([symbol, d]) => ({
        symbol,
        trades: d.trades,
        winRate: d.trades > 0 ? ((d.wins / d.trades) * 100).toFixed(1) : '0',
        totalPL: d.totalPL,
      }))
      .sort((a, b) => Math.abs(b.totalPL) - Math.abs(a.totalPL))
      .slice(0, 8);

    // Compute per-setup stats using tradeRatings
    const setupMap = {};
    weekTrades.forEach(t => {
      const key = `${t.symbol || t.Symbol}-${t.open?.Strike || ''}-${t.open?.Expiration || ''}-${t.open?.ExecTime || ''}`;
      const meta = tradeRatings[key] || {};
      (meta.setups || []).forEach(tag => {
        if (!setupMap[tag]) setupMap[tag] = { trades: 0, wins: 0, totalPL: 0 };
        setupMap[tag].trades++;
        if (t.profitLoss > 0) setupMap[tag].wins++;
        setupMap[tag].totalPL += t.profitLoss;
      });
    });
    const bySetup = Object.entries(setupMap)
      .map(([tag, d]) => ({
        tag,
        trades: d.trades,
        winRate: d.trades > 0 ? ((d.wins / d.trades) * 100).toFixed(1) : '0',
        avgPL: d.trades > 0 ? d.totalPL / d.trades : 0,
      }))
      .sort((a, b) => b.trades - a.trades)
      .slice(0, 6);

    return {
      weeklyStats: { totalPL, totalTrades, winners, losers, winRate, profitFactor, bestDay, worstDay, totalVolume },
      dailyBreakdown,
      tradeCount: totalTrades,
      notes: weekNotes,
      bySymbol,
      bySetup,
    };
  };

  const handleGenerateReview = async (weekKey) => {
    setLoading(true);
    setError(null);
    try {
      const data = computeWeeklyData(weekKey);

      // Find the most recent prior week's goals (from any earlier week)
      const priorWeekGoals = Object.entries(weeklyReviews)
        .filter(([k]) => k < weekKey && weeklyReviews[k]?.goals)
        .sort(([a], [b]) => b.localeCompare(a))[0]?.[1]?.goals || null;

      // Compute current win/loss streak from trades sorted by open date descending
      const tradesSortedDesc = [...trades].sort((a, b) => {
        const da = a.open?.ExecTime || a.openDate || '';
        const db2 = b.open?.ExecTime || b.openDate || '';
        return new Date(db2) - new Date(da);
      });
      let streakContext = null;
      if (tradesSortedDesc.length >= 2) {
        const first = tradesSortedDesc[0].profitLoss > 0 ? 'win' : tradesSortedDesc[0].profitLoss < 0 ? 'loss' : null;
        if (first) {
          let count = 1;
          for (let i = 1; i < tradesSortedDesc.length; i++) {
            const r = tradesSortedDesc[i].profitLoss > 0 ? 'win' : tradesSortedDesc[i].profitLoss < 0 ? 'loss' : null;
            if (r === first) count++; else break;
          }
          if (count >= 2) streakContext = `The trader is currently on a ${count}-trade ${first === 'win' ? 'winning' : 'losing'} streak.`;
        }
      }

      const token = await currentUser.getIdToken();
      const API_URL = process.env.REACT_APP_STRIPE_API_URL || '';
      const response = await fetch(`${API_URL}/api/ai/weekly-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...data, tradingProfile: tradingProfile || null, priorWeekGoals, streakContext }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (response.status === 403) {
          setError('AI Weekly Review is available on the Pro plan.');
        } else if (response.status === 429) {
          setError('AI service is busy. Please try again in a moment.');
        } else {
          setError(errData.error || 'Failed to generate weekly review.');
        }
        return;
      }

      const result = await response.json();
      const reviewEntry = {
        review: result.review,
        goals: result.goals,
        generatedAt: new Date().toISOString(),
        weekLabel: getWeekLabel(new Date(weekKey + 'T00:00:00')),
      };

      setWeeklyReviews(prev => {
        const updated = { ...prev, [weekKey]: reviewEntry };
        if (currentUser) {
          const userDocRef = doc(db, 'users', currentUser.uid);
          updateDoc(userDocRef, { weeklyReviews: updated });
        }
        return updated;
      });

      setExpandedWeeks(prev => ({ ...prev, [weekKey]: true }));
    } catch (err) {
      console.error('Weekly review error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoals = async (weekKey) => {
    setSavingGoals(true);
    try {
      setWeeklyReviews(prev => {
        const updated = {
          ...prev,
          [weekKey]: { ...prev[weekKey], goals: goalsText },
        };
        if (currentUser) {
          const userDocRef = doc(db, 'users', currentUser.uid);
          updateDoc(userDocRef, { weeklyReviews: updated });
        }
        return updated;
      });
      setEditingGoals(null);
    } catch (err) {
      console.error('Save goals error:', err);
    } finally {
      setSavingGoals(false);
    }
  };

  const handleDeleteReview = async (weekKey) => {
    setWeeklyReviews(prev => {
      const updated = { ...prev };
      delete updated[weekKey];
      if (currentUser) {
        const userDocRef = doc(db, 'users', currentUser.uid);
        updateDoc(userDocRef, { weeklyReviews: updated });
      }
      return updated;
    });
  };

  const toggleWeek = (weekKey) => {
    setExpandedWeeks(prev => ({ ...prev, [weekKey]: !prev[weekKey] }));
  };

  if (!isPro) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ width: '64px', height: '64px', borderRadius: '18px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', margin: '0 auto 20px' }}>🔒</div>
        <h2 style={{ color: '#fff', margin: '0 0 10px', fontSize: '20px', fontWeight: 700 }}>Weekly Reviews</h2>
        <p style={{ color: '#6b7f96', fontSize: '13.5px', margin: '0 auto', maxWidth: '320px', lineHeight: 1.65 }}>Upgrade to Pro to unlock AI-powered analysis of your weekly trading performance.</p>
      </div>
    );
  }

  const totalTrades = trades.length;
  const untaggedTotal = trades.filter(t => {
    const r = tradeRatings[getTradeKey(t)] || {};
    return !r.setups?.length && !r.mistakes?.length;
  }).length;

  return (
    <div style={{ padding: '20px', backgroundColor: theme.colors.black }}>
      <style>{`@keyframes aiSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '24px' }}>&#10024;</span>
        <h2 style={{ color: '#fff', margin: 0, fontSize: '22px', fontWeight: '600' }}>Weekly Reviews</h2>
        {isPro && totalTrades > 0 && untaggedTotal > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(45,212,191,0.06)', border: '1px solid rgba(45,212,191,0.18)', borderRadius: '20px', padding: '3px 10px', fontSize: '12px', color: '#8ab4c8' }}>
            <span>⚡</span>
            {untaggedTotal === totalTrades
              ? 'Tag setups & mistakes on your trades for a richer AI review'
              : `${untaggedTotal} of ${totalTrades} trades untagged — tag setups & mistakes for richer AI reviews`}
          </span>
        )}
      </div>

      {error && (
        <div style={{
          backgroundColor: 'rgba(255, 87, 87, 0.15)',
          border: '1px solid rgba(255, 87, 87, 0.3)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: '#ff5757',
          fontSize: '14px',
        }}>
          {error}
        </div>
      )}

      {(showAllWeeks ? weekKeys : weekKeys.slice(0, 10)).map(weekKey => {
        const review = weeklyReviews[weekKey];
        const isCurrent = isCurrentWeek(weekKey);
        const isExpanded = expandedWeeks[weekKey];
        const weekData = computeWeeklyData(weekKey);
        const label = getWeekLabel(new Date(weekKey + 'T00:00:00'));

        return (
          <div
            key={weekKey}
            style={{
              backgroundColor: '#1A2B44',
              border: '1px solid #2B3D55',
              borderLeft: isCurrent ? '3px solid #2DD4BF' : '1px solid #2B3D55',
              borderRadius: '12px',
              marginBottom: '12px',
              overflow: 'hidden',
            }}
          >
            {/* Week header - always visible */}
            <div
              onClick={() => toggleWeek(weekKey)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 20px',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ color: '#8899AA', fontSize: '14px', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block' }}>&#9654;</span>
                <span style={{ color: '#fff', fontWeight: '600', fontSize: '15px' }}>{label}</span>
                {isCurrent && <span style={{ color: '#2DD4BF', fontSize: '11px', backgroundColor: 'rgba(45,212,191,0.1)', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>This Week</span>}
                {weekData.tradeCount > 0 && (
                  <span style={{ color: '#8899AA', fontSize: '13px' }}>
                    {weekData.tradeCount} trades &middot; <span style={{ color: weekData.weeklyStats.totalPL >= 0 ? theme.colors.green : theme.colors.red }}>${weekData.weeklyStats.totalPL.toFixed(2)}</span>
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {review ? (
                  <span style={{ color: '#2DD4BF', fontSize: '12px', fontWeight: 600 }}>✓ Reviewed</span>
                ) : weekData.tradeCount > 0 ? (
                  <span style={{ color: '#8899AA', fontSize: '12px' }}>Ready for review</span>
                ) : null}
              </div>
            </div>

            {/* Expanded content */}
            {isExpanded && (
              <div style={{ padding: '0 20px 20px' }}>
                {!review ? (
                  <div style={{ textAlign: 'center', padding: '24px 0 8px' }}>
                    {weekData.tradeCount === 0 ? (
                      <p style={{ color: '#4a5e72', fontSize: '13.5px', margin: 0 }}>No trades this week.</p>
                    ) : (
                      <button
                        onClick={() => handleGenerateReview(weekKey)}
                        disabled={loading}
                        style={{
                          background: loading ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #2DD4BF 0%, #6366f1 100%)',
                          color: loading ? '#6b7f96' : '#fff',
                          border: 'none',
                          padding: '12px 28px',
                          borderRadius: '10px',
                          fontSize: '13.5px',
                          fontWeight: 700,
                          cursor: loading ? 'not-allowed' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '9px',
                          boxShadow: loading ? 'none' : '0 4px 18px rgba(45,212,191,0.22), 0 2px 8px rgba(99,102,241,0.18)',
                          letterSpacing: 0.2,
                        }}
                      >
                        {loading ? (
                          <>
                            <span style={{ width: '14px', height: '14px', border: '2px solid rgba(45,212,191,0.3)', borderTopColor: '#2DD4BF', borderRadius: '50%', animation: 'aiSpin 0.8s linear infinite', display: 'inline-block' }} />
                            Generating...
                          </>
                        ) : (
                          <><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>Generate Weekly Review</>
                        )}
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Review content */}
                    <div style={{
                      background: 'linear-gradient(160deg, rgba(10,20,42,0.6) 0%, rgba(12,18,38,0.6) 100%)',
                      border: '1px solid rgba(45,212,191,0.1)',
                      borderRadius: '12px',
                      padding: '18px 20px',
                      marginBottom: '14px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '14px', paddingBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ width: '22px', height: '22px', borderRadius: '6px', background: 'linear-gradient(135deg, #2DD4BF, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>✨</div>
                        <span style={{ color: '#fff', fontWeight: 700, fontSize: '12.5px', letterSpacing: 0.1 }}>Weekly Review</span>
                        <span style={{ color: 'rgba(45,212,191,0.7)', fontSize: '10px', background: 'rgba(45,212,191,0.08)', padding: '2px 7px', borderRadius: '20px' }}>GPT-4o</span>
                      </div>
                      <div style={{ color: '#c8d6e5', fontSize: '13.5px', lineHeight: '1.78' }}>
                        {renderMarkdown(review.review)}
                      </div>
                    </div>

                    {/* Goals section */}
                    <div style={{
                      background: 'rgba(45,212,191,0.03)',
                      border: '1px solid rgba(45,212,191,0.12)',
                      borderRadius: '12px',
                      padding: '16px 18px',
                      marginBottom: '14px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2DD4BF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          <span style={{ color: '#2DD4BF', fontWeight: 700, fontSize: '12.5px', letterSpacing: 0.1 }}>Goals for Next Week</span>
                        </div>
                        {editingGoals !== weekKey && (
                          <button
                            onClick={() => { setEditingGoals(weekKey); setGoalsText(review.goals || ''); }}
                            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#7a90a8', padding: '4px 12px', borderRadius: '6px', fontSize: '11.5px', cursor: 'pointer', fontWeight: 500 }}
                          >Edit</button>
                        )}
                      </div>
                      {editingGoals === weekKey ? (
                        <div>
                          <textarea
                            value={goalsText}
                            onChange={(e) => setGoalsText(e.target.value)}
                            style={{
                              width: '100%',
                              minHeight: '110px',
                              background: 'rgba(10,20,42,0.8)',
                              border: '1px solid rgba(45,212,191,0.15)',
                              borderRadius: '8px',
                              color: '#c8d6e5',
                              padding: '12px',
                              fontSize: '13.5px',
                              lineHeight: '1.7',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              boxSizing: 'border-box',
                              outline: 'none',
                            }}
                          />
                          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                            <button
                              onClick={() => handleSaveGoals(weekKey)}
                              disabled={savingGoals}
                              style={{ background: 'linear-gradient(135deg, #2DD4BF 0%, #6366f1 100%)', color: '#fff', border: 'none', padding: '7px 18px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 700, cursor: savingGoals ? 'not-allowed' : 'pointer', boxShadow: '0 2px 10px rgba(45,212,191,0.2)' }}
                            >{savingGoals ? 'Saving...' : 'Save'}</button>
                            <button
                              onClick={() => setEditingGoals(null)}
                              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#7a90a8', padding: '7px 16px', borderRadius: '7px', fontSize: '12.5px', cursor: 'pointer' }}
                            >Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ color: '#c8d6e5', fontSize: '13.5px', lineHeight: '1.72' }}>
                          {review.goals ? renderMarkdown(review.goals) : <span style={{ color: '#4a5e72', fontStyle: 'italic' }}>No goals set yet. Click Edit to add goals for next week.</span>}
                        </div>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => handleGenerateReview(weekKey)}
                        disabled={loading}
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#7a90a8', padding: '6px 14px', borderRadius: '7px', fontSize: '12px', fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        {loading ? (
                          <><span style={{ width: '11px', height: '11px', border: '2px solid rgba(45,212,191,0.3)', borderTopColor: '#2DD4BF', borderRadius: '50%', animation: 'aiSpin 0.8s linear infinite', display: 'inline-block' }} />Regenerating...</>
                        ) : 'Regenerate'}
                      </button>
                      <button
                        onClick={() => handleDeleteReview(weekKey)}
                        style={{ background: 'rgba(255,77,79,0.07)', border: '1px solid rgba(255,77,79,0.18)', color: '#ff6b6b', padding: '6px 14px', borderRadius: '7px', fontSize: '12px', fontWeight: 500, cursor: 'pointer' }}
                      >Delete</button>
                    </div>

                    {review.generatedAt && (
                      <div style={{ color: '#3a4e62', fontSize: '11px', marginTop: '10px' }}>
                        Generated {new Date(review.generatedAt).toLocaleString()}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}

      {!showAllWeeks && weekKeys.length > 10 && (
        <button
          onClick={() => setShowAllWeeks(true)}
          style={{
            display: 'block',
            margin: '0 auto',
            background: 'none',
            border: '1px solid #2B3D55',
            color: '#8899AA',
            padding: '10px 24px',
            borderRadius: '8px',
            fontSize: '13px',
            cursor: 'pointer',
          }}
        >
          View {weekKeys.length - 10} more week{weekKeys.length - 10 === 1 ? '' : 's'}
        </button>
      )}

      {weekKeys.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#8899AA' }}>
          <p>No trading weeks found. Import trade data to get started.</p>
        </div>
      )}
    </div>
  );
};

export default WeeklyReviewScreen;
